import { useState } from 'react';
import { usePartnerSelector, usePartnerDispatch } from '../../store';
import {
  selectLocalCartItems,
  selectLocalCartCount,
  selectLocalCartTotalCents,
  removeLocalCartItem,
  type LocalCartItem
} from '../../store/localCartSlice';
import { resetItem, resetFields } from 'configurator-ui-shared';
import { formatPrice } from '../../utils/formatPrice';
import { fetchCheckoutUrl } from '../../utils/checkoutApi';

/**
 * LocalCartTab
 *
 * Cart / Checkout tab panel for the embedded configurator.
 * Displays locally-queued configured items before they are sent to Shopify.
 * On checkout, POSTs to /cart/checkout-url and redirects the top frame.
 */
interface LocalCartTabProps {
  onCreateAnotherLabelSet?: () => void;
}

export function LocalCartTab({ onCreateAnotherLabelSet }: LocalCartTabProps) {
  const dispatch = usePartnerDispatch();
  const items = usePartnerSelector(selectLocalCartItems);
  const count = usePartnerSelector(selectLocalCartCount);
  const totalCents = usePartnerSelector(selectLocalCartTotalCents);

  const [isLoading, setIsLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const handleCheckout = async () => {
    setIsLoading(true);
    setCheckoutError(null);
    try {
      const rfsn =
        new URLSearchParams(window.location.search).get('rfsn') ?? undefined;
      const { checkoutUrl, cartId } = await fetchCheckoutUrl(
        items.map(i => ({
          variantId: i.variantId,
          quantity: i.quantity,
          properties: i.properties
        })),
        rfsn
      );
      // Notify Refersion of the checkout before the user leaves the page
      try {
        const r = (
          window as Window & {
            r?: { sendCheckoutEvent?: (...args: unknown[]) => void };
          }
        ).r;
        if (r?.sendCheckoutEvent) {
          // Strip the ?key= query parameter from the cart GID before extracting the token.
          // Cart ID format: gid://shopify/Cart/TOKEN?key=KEY — we only want TOKEN.
          // This mirrors Refersion's own parsing from their headless integration guide.
          const cartUrl = new URL(cartId);
          cartUrl.searchParams.delete('key');
          const cartToken = cartUrl.toString().split('/').pop() ?? cartId;
          r.sendCheckoutEvent(
            cartToken,
            localStorage.getItem('rfsn_v4_id'),
            window.location.href,
            localStorage.getItem('rfsn_v4_aid'),
            localStorage.getItem('rfsn_v4_cs')
          );
        }
      } catch {
        // Non-fatal — don't block checkout if Refersion tracking fails
      }
      window.open(checkoutUrl, '_blank');
      setIsLoading(false);
      // TODO: Remove below when decision is made about how we want checkout button to function
      // dispatch(clearLocalCart());
      // window.top!.location.href = checkoutUrl;
    } catch {
      setIsLoading(false);
      setCheckoutError('Checkout failed. Please try again.');
    }
  };

  return (
    <div className="flex flex-col">
      <div className="p-4">
        {count === 0 ? (
          <div className="text-light-gray flex flex-col items-center justify-center px-6 py-12 text-center">
            <svg
              className="text-lighter-gray mb-4 h-12 w-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            <p className="text-sm">
              Your cart is empty. Configure a product to get started.
            </p>
          </div>
        ) : (
          <ul className="divide-lighter-gray divide-y">
            {items.map(item => (
              <LocalCartItemRow
                key={item.localId}
                item={item}
                onRemove={() => dispatch(removeLocalCartItem(item.localId))}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Sticky footer: total + checkout */}
      {count > 0 && (
        <div
          className="border-lighter-gray border-t p-4"
          style={{ backgroundColor: 'var(--nb-body-bg, #ffffff)' }}
        >
          {/* Total row */}
          <div className="mb-3 flex items-center justify-between">
            <span className="text-dark-gray text-sm font-semibold">Total</span>
            <span className="text-dark-gray text-sm font-bold">
              {formatPrice(totalCents)}
            </span>
          </div>

          {/* Create Another Label Set button */}
          <button
            onClick={() => {
              dispatch(resetItem());
              dispatch(resetFields());
              onCreateAnotherLabelSet?.();
            }}
            className="border-secondary-button-text bg-secondary-button text-secondary-button-text mb-2 flex w-full items-center justify-center rounded-md border px-6 py-3 text-sm font-semibold transition-all hover:brightness-95"
          >
            Create Another Label Set
          </button>

          {/* Checkout button */}
          <button
            onClick={handleCheckout}
            disabled={isLoading}
            className="bg-primary-button text-primary-button-text flex w-full items-center justify-center gap-2 rounded-md px-6 py-3 text-sm font-semibold transition-all hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isLoading && (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
            )}
            {isLoading ? 'Processing…' : 'Checkout'}
          </button>

          {/* Inline error */}
          {checkoutError && (
            <p className="text-error mt-2 text-center text-xs">
              {checkoutError}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// LocalCartItemRow
// ---------------------------------------------------------------------------

interface LocalCartItemRowProps {
  item: LocalCartItem;
  onRemove: () => void;
}

const Divider = () => <span className="text-xl">&#183;</span>;

function LocalCartItemRow({ item, onRemove }: LocalCartItemRowProps) {
  const style = item.properties.Style;
  const color = item.properties.Color;
  const firstName = item.properties['First Name'];
  const lastName = item.properties['Last Name'];
  const singleName = item.properties['Full Name'] || item.properties.Name;
  const hasDetails = style || color || firstName || lastName || singleName;

  return (
    <li className="flex items-center gap-3 py-3">
      {/* Thumbnail */}
      {item.thumbnailUrl && (
        <img
          src={item.thumbnailUrl}
          alt={item.displayName}
          className="border-lighter-gray h-12 w-12 shrink-0 rounded border object-cover"
        />
      )}

      {/* Main content row */}
      <div className="flex min-w-0 flex-1 items-center justify-between gap-4 align-middle">
        {/* Left: name + price */}
        <div className="flex items-center gap-4">
          <p className="text-dark-gray truncate text-sm font-semibold">
            {item.displayName}
          </p>

          {/* Right: style/color row, then first/last name row */}
          {hasDetails && (
            <div className="text-dark-gray mt-0.5 min-w-0 items-center text-xs">
              {(style || color || firstName || lastName) && (
                <div className="flex items-center gap-2">
                  {style && (
                    <>
                      <span className="truncate">{style}</span>
                      <Divider />
                    </>
                  )}

                  {color && (
                    <>
                      <span className="truncate">{color}</span>
                      <Divider />
                    </>
                  )}

                  {firstName && (
                    <>
                      <span className="truncate">{firstName}</span>
                      <Divider />
                    </>
                  )}
                  {lastName && <span className="truncate">{lastName}</span>}
                </div>
              )}
            </div>
          )}
        </div>
        <p className="text-dark-gray mt-0.5 flex items-center gap-1.5 text-xs">
          {item.compareAtPriceCents ? (
            <>
              <span className="line-through opacity-60">
                {formatPrice(item.compareAtPriceCents)}
              </span>
              <span className="text-coral font-semibold">
                {formatPrice(item.priceCents)}
              </span>
            </>
          ) : (
            formatPrice(item.priceCents)
          )}
        </p>
      </div>

      {/* Remove button */}
      <button
        onClick={onRemove}
        aria-label={`Remove ${item.displayName}`}
        className="border-lighter-gray text-dark-gray hover:text-coral hover:border-coral flex h-7 w-7 shrink-0 items-center justify-center rounded border transition-colors"
      >
        <svg
          className="h-3.5 w-3.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
          />
        </svg>
      </button>
    </li>
  );
}
